import { Button, Dialog, DialogContent, DialogTitle, Stack, Grid, Box, DialogActions } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useContext, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import CustomLoader from "../../../../../../../util/component/CustomLoader";
import FileUploader from "../../../../../../../util/component/FileUploader";
import { setSnackbar } from "../../../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../../../util/state/globalVariables";
import { uploadBookingAttachment, useBookingAttachmentsQuery, useAttachmentTypeQuery, useBookingByIdWithMetaQueryObserver, IResponseAttachmentType } from "../../../../../api/services";
import DialogCloseButton from "../../../../../components/DialogCloseButton";
import useDialog from "../../../../../hooks/useDialog";
import { BookingScope, ReactQueryKey, RouteParams } from "../../../../../types";
import { hasScope } from "../../../../../utils";
import TableAttachments from "./table-attachments/TableAttachments";
import { FormAutocomplete } from "../../../../../components/fields";
import { IFormValues, validationSchema } from "./form";

const TabAttachments = () => {
    const { dispatchGlobalData, globalData } = useContext(GlobalContext);
    const { id } = useParams<RouteParams>();
    const { t } = useTranslation('booking');
    const newAttachmentDialog = useDialog();
    const bookingQuery = useBookingByIdWithMetaQueryObserver(id);
    const queryClient = useQueryClient();

    const { control, setValue, handleSubmit, reset, getValues } = useForm<IFormValues>({
        defaultValues: {
            type: null,
            file: null
        },
        reValidateMode: 'onSubmit',
        resolver: yupResolver(validationSchema, {}, { rawValues: true })
        });

    const attachmentsQuery = useBookingAttachmentsQuery(
        id,
        {
            refetchOnMount: false
        }
    );

    const attachmentTypeQuery = useAttachmentTypeQuery(
         id,
        {
            refetchOnMount: false
        }
    );
    const addBookingAttachmentMutation = useMutation(
        uploadBookingAttachment,
        {
            onSuccess: () => {
                attachmentsQuery.refetch();
                queryClient.invalidateQueries({
                    queryKey: [ReactQueryKey.BookingById, id]
                })
            },
            onError: () => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                })
            },
            onSettled: () => {
                handleNewAttachmentDialogClose();
            }
        }
    );

    const handleUpload = useCallback(() => {
        if (!id) {
            return;
        }
        const type = getValues('type')?.attachmentType || '';
        addBookingAttachmentMutation.mutate({
            bookingId: id,
            file: getValues('file'),
            type
        });
    }, [id]);
    const handleNewAttachmentDialogClose = useCallback(() => {
        reset();
        newAttachmentDialog.handleClose();
    }, []);

    return (
        <Stack rowGap={2}>
            <Stack direction="row" justifyContent="end">
                {hasScope(globalData, BookingScope.UploadAttachment) &&
                    <Button variant="outlined" onClick={newAttachmentDialog.handleShow}>
                        {t('label.newAttachment')}
                    </Button>
                }
                <Dialog open={newAttachmentDialog.open} maxWidth="sm" fullWidth>
                    <DialogCloseButton onClick={handleNewAttachmentDialogClose} />
                    <DialogTitle>{t("label.newAttachment")}</DialogTitle>
                    <DialogContent>
                        <Box>
                            <Controller
                                control={control}
                                name="file"
                                render={({field, fieldState}) => (
                                    <Fragment>
                                        <FileUploader
                                            onUpload={(file: File) => {
                                                field.onChange(file);
                                                setValue("file", file);
                                            }}
                                            isDirectUpload={false}
                                            onRemove={() => field.onChange(null)}
                                        />
                                        { Boolean(fieldState.error) &&
                                            <p style={{fontWeight: 400, fontSize: "0.75rem", color: "red"}}>{t(`${fieldState?.error?.message}`)}</p>
                                        }
                                    </Fragment>
                                )}
                            />
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item container id="main-data-row" md={12} xs={12} columnSpacing={3} rowSpacing={4}>
                                <Grid item xs={12}>
                                    <FormAutocomplete<IFormValues>
                                        control={control}
                                        name="type"
                                        label={t('label.fileType')}
                                        options={attachmentTypeQuery.data ?? []}
                                        loading={attachmentTypeQuery.isLoading}
                                        getOptionLabel={(option: IResponseAttachmentType) => { return !!option.attachmentType && t(`asset_type.${option.attachmentType}`) ? t(`asset_type.${option.attachmentType}`) : ''}}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={handleNewAttachmentDialogClose}>
                            {t("label.cancel")}
                        </Button>
                        <Button onClick={handleSubmit(handleUpload)}
                                disabled={addBookingAttachmentMutation.isLoading ||
                                attachmentTypeQuery.isLoading}>
                            {t("label.confirm")}
                        </Button>
                    </DialogActions>
                </Dialog>
                <CustomLoader loading={addBookingAttachmentMutation.isLoading || attachmentTypeQuery.isLoading} />
            </Stack>
            <TableAttachments />
        </Stack>
    );
};

export default TabAttachments;
